/**
 * HeaderFragment - Renders header from generated HTML fragment with React hydration
 * 
 * This component:
 * - Imports the generated header HTML fragment
 * - Injects React navigation components at placeholder points
 * - Manages mobile menu state
 * - Handles auth-aware navigation
 * - Overrides positioning to always be fixed (regardless of static site config)
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import parse, { domToReact } from 'html-react-parser';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { getPublicPageUrl, getLoginUrl } from '../../../config/urls';
import { appConfigService } from '../../../services/appConfig';
import { useNotificationService } from '../../../services/notificationService';
import { NoticeBar } from '../common';
import navigationConfig from '../../../config/navigation.json';
import siteConfig from '../../../config/site.json';
import headerHtml from '../../../assets/generated/header.fragment.html?raw';

const ADMIN_ROLE = 40;
const MANAGER_ROLE = 30;

const HeaderFragment = () => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, authState, logout, loading } = useAuth();
  const menuRef = useRef(null);
  const location = useLocation();
  const config = useConfig();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Feature flags
  const queryParams = new URLSearchParams(location.search);
  const showDashboardOverride = queryParams.get('showDashboard') === '1';
  const isDashboardEnabled = config.app?.dashboardEnabled === true || showDashboardOverride;
  const isNotificationsEnabled = appConfigService.isFeatureEnabled('notifications');
  const isAppEnabled = siteConfig.components?.app === true;

  // Notification count (only if feature enabled)
  const { unreadCount } = useNotificationService(
    isNotificationsEnabled ? (user?.internalId || null) : null
  );

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
      // On Vite dev server there's no marketing site at /, so go to /login
      const isViteDevServer = import.meta.env.DEV || import.meta.env.MODE === 'development';
      window.location.href = isViteDevServer ? '/login' : '/';
    }
  }, [logout]);

  // Render desktop navigation based on auth state
  const renderDesktopNav = useCallback(() => {
    if (authState === 'authenticated' || authState === 'offline') {
      return (
        <nav className="hidden sm:flex flex-nowrap items-center gap-6 text-lg" aria-label="Main navigation">
          <Link
            to="/home"
            className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
          >
            Home
          </Link>
          {isDashboardEnabled && (
            <Link
              to="/dashboard"
              className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
            >
              Dashboard
            </Link>
          )}
          {user?.role >= MANAGER_ROLE && (
            <Link
              to="/manage"
              className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
            >
              Manage
            </Link>
          )}
          {user?.role === ADMIN_ROLE && (
            <Link
              to="/admin"
              className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
            >
              Admin
            </Link>
          )}
          {isNotificationsEnabled && (
            <Link
              to="/notifications"
              className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors relative`}
            >
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
          )}
          <Link
            to="/profile"
            className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
          >
            Profile
          </Link>
          <button
            onClick={handleLogout}
            className={`px-4 py-2 rounded-lg border ${semantic.headerButtonBorder} ${semantic.headerButtonBg} ${semantic.headerButtonText} ${semantic.headerButtonHover} text-sm font-medium transition-colors`}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </button>
        </nav>
      );
    }

    // Unauthenticated: show public navigation from config
    const navItems = navigationConfig.items.filter(item => item.enabled !== false);
    return (
      <nav className="hidden sm:flex flex-nowrap items-center gap-6 text-lg" aria-label="Main navigation">
        {navItems.map(item => (
          <a
            key={item.id || item.label}
            href={item.href.startsWith('http') ? item.href : getPublicPageUrl(item.href.replace('.html', ''))}
            className={`${semantic.navLink} ${semantic.navLinkHover} no-underline transition-colors`}
          >
            {replace(item.label)}
          </a>
        ))}
        {isAppEnabled && (
          <a
            href={getLoginUrl()}
            className={`px-4 py-2 rounded-lg border ${semantic.headerButtonBorder} ${semantic.headerButtonBg} ${semantic.headerButtonText} ${semantic.headerButtonHover} text-sm font-medium transition-colors`}
            onMouseEnter={() => import('../../pages/auth/LoginPage')}
          >
            Login/Register
          </a>
        )}
      </nav>
    );
  }, [authState, user, isDashboardEnabled, isNotificationsEnabled, isAppEnabled, unreadCount, semantic, replace, handleLogout, isLoggingOut]);

  // Render mobile navigation
  const renderMobileNav = useCallback(() => {
    const handleLinkClick = () => {
      setIsMenuOpen(false);
    };

    if (authState === 'authenticated' || authState === 'offline') {
      return (
        <>
          <Link
            to="/profile"
            className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
            onClick={handleLinkClick}
          >
            Profile
          </Link>
          {isNotificationsEnabled && (
            <Link
              to="/notifications"
              className={`py-4 text-2xl font-light ${semantic.headerText} no-underline flex items-center gap-2`}
              onClick={handleLinkClick}
            >
              Messages
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
          )}
          <Link
            to="/home"
            className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
            onClick={handleLinkClick}
          >
            Home
          </Link>
          {isDashboardEnabled && (
            <Link
              to="/dashboard"
              className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
              onClick={handleLinkClick}
            >
              Dashboard
            </Link>
          )}
          {user?.role >= MANAGER_ROLE && (
            <Link
              to="/manage"
              className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
              onClick={handleLinkClick}
            >
              Manage
            </Link>
          )}
          {user?.role === ADMIN_ROLE && (
            <Link
              to="/admin"
              className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
              onClick={handleLinkClick}
            >
              Admin
            </Link>
          )}
          <button
            onClick={() => {
              handleLogout();
              handleLinkClick();
            }}
            className={`w-full py-6 text-center ${semantic.headerButtonBg} ${semantic.headerButtonText} ${semantic.headerButtonHover} text-2xl`}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </button>
        </>
      );
    }

    // Unauthenticated mobile nav
    return (
      <>
        <a
          href={getPublicPageUrl('home')}
          className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
          onClick={handleLinkClick}
        >
          Home
        </a>
        <a
          href={getPublicPageUrl('about')}
          className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
          onClick={handleLinkClick}
        >
          About
        </a>
        <a
          href={getPublicPageUrl('contact')}
          className={`py-4 text-2xl font-light ${semantic.headerText} no-underline`}
          onClick={handleLinkClick}
        >
          Contact
        </a>
        {isAppEnabled && (
          <a
            href={getLoginUrl()}
            className={`w-full py-6 text-center ${semantic.headerButtonBg} ${semantic.headerButtonText} ${semantic.headerButtonHover} text-2xl`}
            onClick={handleLinkClick}
            onMouseEnter={() => import('../../pages/auth/LoginPage')}
          >
            Login/Register
          </a>
        )}
      </>
    );
  }, [authState, user, isDashboardEnabled, isNotificationsEnabled, isAppEnabled, unreadCount, semantic, handleLogout, isLoggingOut]);

  // Parser options for HTML transformation.
  // Fragment HTML is from builder/sync-fragments (valid HTML with "class"). When we create
  // React elements in replace(), map HTML attribs to React props (e.g. class → className).
  const parserOptions = useMemo(() => ({
    replace: (domNode) => {
      // Override header positioning: React app always uses fixed header
      // (static site may use relative based on config)
      if (domNode.name === 'header' && domNode.attribs?.class) {
        const originalClass = domNode.attribs.class;
        // Replace 'relative' with 'fixed top-0' and adjust padding
        const newClass = originalClass
          .replace(/\brelative\b/, 'fixed top-0')
          .replace(/\bpy-4\b/, 'pt-0 pb-2');

        return (
          <header className={newClass}>
            {domToReact(domNode.children, parserOptions)}
          </header>
        );
      }

      // Inject desktop navigation
      if (domNode.attribs?.id === 'react-desktop-nav-placeholder') {
        return renderDesktopNav();
      }

      // Inject mobile navigation
      if (domNode.attribs?.id === 'react-mobile-nav-placeholder') {
        return renderMobileNav();
      }

      // Attach hamburger button handler and toggle icon
      if (domNode.attribs?.id === 'hamburger-button') {
        const { class: className, ...restAttribs } = domNode.attribs;
        return (
          <button
            {...restAttribs}
            className={className}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
        );
      }

      // Control mobile menu visibility with React state
      if (domNode.attribs?.id === 'mobile-menu') {
        const { class: className, ...restAttribs } = domNode.attribs;
        // Remove existing visibility/animation classes, apply based on state
        const baseClass = className
          .replace(/\bhidden\b/g, '')
          .replace(/\bflex\b/g, '')
          .replace(/\banimate-open-menu\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const visibilityClass = isMenuOpen ? 'flex animate-open-menu' : 'hidden';

        return (
          <section
            {...restAttribs}
            ref={menuRef}
            className={`${baseClass} ${visibilityClass}`}
          >
            {domToReact(domNode.children, parserOptions)}
          </section>
        );
      }

      // Convert internal links to React Router (HTML 'class' → React 'className')
      if (domNode.name === 'a' && domNode.attribs?.href?.startsWith('/')) {
        const { href, class: className, ...restAttribs } = domNode.attribs;
        // Root path: use plain anchor so the logo does a full page load to the static marketing site
        if (href === '/' || href === '') {
          return (
            <a href="/" className={className} {...restAttribs}>
              {domToReact(domNode.children, parserOptions)}
            </a>
          );
        }
        return (
          <Link to={href} className={className} {...restAttribs}>
            {domToReact(domNode.children, parserOptions)}
          </Link>
        );
      }
    }
  }), [isMenuOpen, toggleMenu, renderDesktopNav, renderMobileNav]);

  return (
    <>
      {parse(headerHtml, parserOptions)}
      <NoticeBar authState={authState} user={user} loading={loading} />
    </>
  );
};

export default HeaderFragment;
